import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "loadingIndicator", "loadMoreButton", "spinner"]
  static values = {
    url: String,
    page: Number,
    loading: Boolean,
    hasMore: { type: Boolean, default: true },
    batchSize: { type: Number, default: 15 }
  }
  
  connect() {
    // Update the map with initial providers
    this.dispatchMapUpdate();
  }
  
  disconnect() {
    this.cancelPendingRequest();
  }

  cancelPendingRequest() {
    this.paginationRequest?.abort();
    this.paginationRequest = null;
    this.loadingValue = false;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add('hidden');
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.disabled = false;
  }

  resetPagination(event) {
    this.cancelPendingRequest();
    this.pageValue = 1;
    this.hasMoreValue = Boolean(event.detail.hasMore);
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !this.hasMoreValue);
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && this.hasMoreValue && !this.loadingValue) {
        this.loadMore();
      }
    });
  }
  
  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue) return;

    const request = new AbortController();
    this.paginationRequest = request;
    
    this.loadingValue = true;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden");
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.disabled = true;
    
    // Build URL with current params and page
    const nextPage = this.pageValue + 1;
    const url = new URL(this.urlValue, window.location.origin);
    const params = new URLSearchParams(url.search);
    
    // Preserve all current search params - handle both URL and browser address bar
    const currentParams = new URLSearchParams(window.location.search);
    
    // Combine both parameter sources to ensure we get everything
    const allParams = new Set([...params.keys(), ...currentParams.keys()]);
    allParams.forEach(key => {
      // Skip 'page' parameter since we'll set it explicitly
      if (key !== 'page') {
        // Prioritize params from URL, fall back to browser address bar
        const value = params.get(key) || currentParams.get(key);
        if (value) {
          params.set(key, value);
        }
      }
    });
    
    // Make sure query parameters are preserved
    if (currentParams.has('query')) {
      params.set('query', currentParams.get('query'));
    }
    
    // Ensure we have lat/lng for map-based searches
    if (currentParams.has('lat') && currentParams.has('lng')) {
      params.set('lat', currentParams.get('lat'));
      params.set('lng', currentParams.get('lng'));
    }
    params.set('page', nextPage);
    url.search = params.toString();
    
    try {
      const response = await fetch(url, {
        signal: request.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
      const data = await response.json();
      if (this.paginationRequest !== request) return;
      this.hasMoreValue = data.has_more;

      if (data.html) {
        // Append cards to the existing grid inside the container
        const existingGrid = this.containerTarget.querySelector('.grid');
        if (existingGrid) {
          // Extract only the card elements from the returned HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = data.html;
          const newCards = tempDiv.querySelectorAll('.provider-card');
          newCards.forEach(card => existingGrid.appendChild(card));
        } else {
          // Fallback: no grid found, just append
          this.containerTarget.insertAdjacentHTML('beforeend', data.html);
        }
        
        this.pageValue = nextPage;
        
        if (data.providers && Array.isArray(data.providers) && data.providers.length > 0) {
          this.dispatchMapUpdate(data.providers);
          this.dispatchGlobalMapEvent(data.providers);
        }
        this.refreshStimulusControllers();
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading more providers:", error);
    } finally {
      if (this.paginationRequest === request) {
        this.paginationRequest = null;
        this.loadingValue = false;
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden");
        if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.disabled = false;
        if (this.hasLoadingIndicatorTarget) {
          this.loadingIndicatorTarget.classList.toggle('hidden', !this.hasMoreValue);
        }
      }
    }
  }
  
  // Add a new method to dispatch a global window event
  dispatchGlobalMapEvent(newProviders) {
    if (newProviders && Array.isArray(newProviders) && newProviders.length > 0) {
      // Create and dispatch a global event that any controller can listen for
      const event = new CustomEvent('map:addMarkers', {
        detail: { providers: newProviders }
      });
      
      window.dispatchEvent(event);
    }
  }
  
  // Improved event dispatch for local controller communication
  dispatchMapUpdate(newProviders = null) {
    // Only dispatch the event if we have valid data
    if (newProviders === null || (Array.isArray(newProviders) && newProviders.length > 0)) {
      // Try multiple approaches to ensure the event is received
      
      // 1. Standard event bubbling
      const event = new CustomEvent('providers:loaded', {
        detail: { providers: newProviders },
        bubbles: true
      });
      
      this.element.dispatchEvent(event);
      
      // 2. Direct targeting of the map controller if we can find it
      const mapController = document.querySelector('[data-controller="map"]');
      if (mapController) {
        const directEvent = new CustomEvent('providers:loaded', {
          detail: { providers: newProviders }
        });
        mapController.dispatchEvent(directEvent);
      }
    }
  }
  
  refreshStimulusControllers() {
    try {
      if (window.Stimulus) {
        if (window.Stimulus.application && window.Stimulus.application.controllers) {
          window.Stimulus.application.controllers.forEach(controller => {
            if (controller && controller.element && document.body.contains(controller.element)) {
              controller.disconnect();
              controller.connect();
            }
          });
        }
      } else {
        const stimulusApp = window.StimulusReflex ? window.StimulusReflex.application : null;
        if (stimulusApp) {
          stimulusApp.controllers.forEach(controller => {
            if (controller && controller.element && document.body.contains(controller.element)) {
              controller.disconnect();
              controller.connect();
            }
          });
        }
      }
    } catch (error) {
      console.error("Error refreshing Stimulus controllers:", error);
    }
  }
}
